/**
 * Callback Request Modal
 * Allows customers to request a callback from the sales team
 */
export class CallbackModal {
    constructor() {
        this.modal = null;
        this.overlay = null;
        this.modalContent = null;
        this.availableDates = [];
        this.currentStep = 'form';
        this.init();
    }
    init() {
        this.createModal();
        this.attachTriggers();
        this.checkUrlParams();
    }
    checkUrlParams() {
        // Check for ?callback=open parameter on page load
        if (window.location.search.includes('callback=open')) {
            setTimeout(() => this.open(), 500); // Small delay for page load
        }
    }
    createModal() {
        // Create modal overlay
        this.overlay = document.createElement('div');
        this.overlay.className = 'callback-modal-overlay';
        this.overlay.style.display = 'none';
        // Create modal container
        this.modal = document.createElement('div');
        this.modal.className = 'callback-modal';
        // Create modal content
        this.modalContent = document.createElement('div');
        this.modalContent.className = 'callback-modal-content';
        this.modal.appendChild(this.modalContent);
        this.overlay.appendChild(this.modal);
        document.body.appendChild(this.overlay);
        // Close on overlay click
        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay) {
                this.close();
            }
        });
        // Close on ESC key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.overlay?.style.display === 'flex') {
                this.close();
            }
        });
    }
    attachTriggers() {
        document.querySelectorAll('[data-callback-trigger]').forEach((trigger) => {
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                this.open();
            });
        });
    }
    async open() {
        if (!this.overlay)
            return;
        this.overlay.style.display = 'flex';
        setTimeout(() => this.overlay?.classList.add('is-open'), 10);
        // Show loading while fetching availability
        this.renderLoading();
        try {
            await this.fetchAvailability();
            this.renderForm();
        }
        catch (error) {
            console.error('Failed to load availability:', error);
            this.showError('Failed to load available dates. Please try again.');
        }
    }
    close() {
        if (!this.overlay)
            return;
        this.overlay.classList.remove('is-open');
        setTimeout(() => {
            if (this.overlay) {
                this.overlay.style.display = 'none';
            }
        }, 300);
    }
    async fetchAvailability() {
        const response = await fetch('/api/callback-request/availability');
        if (!response.ok) {
            throw new Error('Failed to fetch availability');
        }
        const data = await response.json();
        this.availableDates = (data.data || []).map(d => ({
            date: d.date,
            dayOfWeek: new Date(d.date).toLocaleDateString('en-US', { weekday: 'long' }),
            formatted: new Date(d.date).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric'
            }),
            isFullyBooked: d.isFullyBooked
        }));
    }
    renderLoading() {
        if (!this.modalContent)
            return;
        this.modalContent.innerHTML = `
      <div class="callback-modal-loading">
        <div class="callback-spinner"></div>
        <p>Loading available dates...</p>
      </div>
    `;
    }
    renderForm() {
        if (!this.modalContent)
            return;
        // Pre-fill from session/localStorage if available
        const userData = this.getUserData();
        this.modalContent.innerHTML = `
      <div class="callback-modal-header">
        <h2>Request a Callback</h2>
        <button type="button" class="callback-modal-close" aria-label="Close">&times;</button>
      </div>

      <div class="callback-modal-body">
        <form id="callback-form" class="callback-form">
          <div class="callback-form-group">
            <label for="callback-name">Name <span class="required">*</span></label>
            <input
              type="text"
              id="callback-name"
              name="name"
              required
              value="${userData.name || ''}"
              placeholder="Your full name"
            />
            <span class="form-error"></span>
          </div>

          <div class="callback-form-row">
            <div class="callback-form-group">
              <label for="callback-email">Email <span class="required">*</span></label>
              <input
                type="email"
                id="callback-email"
                name="email"
                required
                value="${userData.email || ''}"
              />
              <span class="form-error"></span>
            </div>

            <div class="callback-form-group">
              <label for="callback-phone">Phone <span class="required">*</span></label>
              <input
                type="tel"
                id="callback-phone"
                name="phone"
                placeholder="(555) 123-4567"
                required
                value="${userData.phone || ''}"
              />
              <span class="form-error"></span>
            </div>
          </div>

          <div class="callback-form-group">
            <label for="callback-company">Company</label>
            <input
              type="text"
              id="callback-company"
              name="company"
              value="${userData.company || ''}"
            />
          </div>

          <div class="callback-form-group">
            <label for="callback-reason">Reason for Callback <span class="required">*</span></label>
            <select id="callback-reason" name="reason" required>
              <option value="">Select a reason...</option>
              <option value="Order Inquiry">Order Inquiry</option>
              <option value="Product Question">Product Question</option>
              <option value="Returns & Exchanges">Returns & Exchanges</option>
              <option value="Design Tool Help">Design Tool Help</option>
              <option value="Wholesale/Bulk Pricing">Wholesale/Bulk Pricing</option>
              <option value="Partnership">Partnership</option>
              <option value="Other">Other</option>
            </select>
            <span class="form-error"></span>
          </div>

          <div class="callback-form-group">
            <label for="callback-notes">Additional Notes</label>
            <textarea
              id="callback-notes"
              name="notes"
              rows="3"
              placeholder="Any additional information you'd like to share..."
            ></textarea>
          </div>

          <div class="callback-form-group">
            <label for="callback-date">Preferred Date <span class="required">*</span></label>
            <select id="callback-date" name="requestedDate" required>
              <option value="">Select a date...</option>
              ${this.availableDates.map(date => `
                <option value="${date.date}">${date.formatted} (${date.dayOfWeek})</option>
              `).join('')}
            </select>
            <span class="form-error"></span>
          </div>

          <div class="callback-form-group">
            <label>Preferred Time <span class="required">*</span></label>
            <div class="callback-time-options">
              <label class="callback-radio-label">
                <input type="radio" name="requestedTimeRange" value="morning" required />
                <span>Morning (9am-12pm ET)</span>
              </label>
              <label class="callback-radio-label">
                <input type="radio" name="requestedTimeRange" value="afternoon" required />
                <span>Afternoon (12pm-5pm ET)</span>
              </label>
              <label class="callback-radio-label">
                <input type="radio" name="requestedTimeRange" value="evening" required />
                <span>Evening (5pm-8pm ET)</span>
              </label>
            </div>
            <span class="form-error"></span>
          </div>
        </form>
      </div>

      <div class="callback-modal-footer">
        <button type="button" class="callback-btn callback-btn-secondary" id="callback-cancel-btn">
          Cancel
        </button>
        <button type="submit" class="callback-btn callback-btn-primary" id="callback-submit-btn" form="callback-form">
          Request Callback
        </button>
      </div>
    `;
        this.attachFormListeners();
    }
    attachFormListeners() {
        // Close button
        const closeBtn = this.modalContent?.querySelector('.callback-modal-close');
        closeBtn?.addEventListener('click', () => this.close());
        // Cancel button
        const cancelBtn = document.getElementById('callback-cancel-btn');
        cancelBtn?.addEventListener('click', () => this.close());
        // Form submission
        const form = document.getElementById('callback-form');
        form?.addEventListener('submit', (e) => this.handleSubmit(e));
        // Phone formatting
        const phoneInput = document.getElementById('callback-phone');
        phoneInput?.addEventListener('input', (e) => this.formatPhone(e));
        // Real-time validation on blur
        const inputs = form?.querySelectorAll('input, select, textarea');
        inputs?.forEach((input) => {
            input.addEventListener('blur', () => this.validateField(input));
        });
    }
    formatPhone(e) {
        const input = e.target;
        let value = input.value.replace(/\D/g, '');
        if (value.length > 0) {
            if (value.length <= 3) {
                value = `(${value}`;
            }
            else if (value.length <= 6) {
                value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
            }
            else {
                value = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
            }
        }
        input.value = value;
    }
    validateField(field) {
        const errorEl = field.parentElement?.querySelector('.form-error');
        if (!errorEl)
            return true;
        errorEl.textContent = '';
        field.classList.remove('is-invalid');
        const value = field.value.trim();
        const isRequired = field.hasAttribute('required');
        if (isRequired && !value) {
            this.showFieldError(field, errorEl, 'This field is required');
            return false;
        }
        if (field.type === 'email' && value && !this.isValidEmail(value)) {
            this.showFieldError(field, errorEl, 'Please enter a valid email');
            return false;
        }
        if (field.type === 'tel' && value && !this.isValidPhone(value)) {
            this.showFieldError(field, errorEl, 'Please enter a valid phone number');
            return false;
        }
        // Validate radio buttons
        if (field.type === 'radio' && isRequired) {
            const radioGroup = document.getElementsByName(field.name);
            const isChecked = Array.from(radioGroup).some(radio => radio.checked);
            if (!isChecked) {
                const container = field.closest('.callback-form-group');
                const containerError = container?.querySelector('.form-error');
                if (containerError) {
                    this.showFieldError(field, containerError, 'Please select a preferred time');
                }
                return false;
            }
        }
        return true;
    }
    showFieldError(field, errorEl, message) {
        field.classList.add('is-invalid');
        errorEl.textContent = message;
    }
    isValidEmail(email) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }
    isValidPhone(phone) {
        const digits = phone.replace(/\D/g, '');
        return digits.length === 10;
    }
    async handleSubmit(e) {
        e.preventDefault();
        const form = e.target;
        const formData = new FormData(form);
        // Validate all fields
        let isValid = true;
        const inputs = form.querySelectorAll('input, select, textarea');
        inputs.forEach((input) => {
            if (!this.validateField(input)) {
                isValid = false;
            }
        });
        if (!isValid)
            return;
        // Build request data
        // Split name into first and last name (backend expects separate fields)
        const fullName = formData.get('name').trim();
        const nameParts = fullName.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
        const data = {
            firstName,
            lastName,
            email: formData.get('email'),
            phone: formData.get('phone'),
            company: formData.get('company') || undefined,
            reason: formData.get('reason'),
            notes: formData.get('notes') || undefined,
            requestedDate: formData.get('requestedDate'),
            requestedTimeRange: formData.get('requestedTimeRange'),
        };
        const submitBtn = document.getElementById('callback-submit-btn');
        submitBtn.disabled = true;
        submitBtn.innerHTML = `
      <span class="callback-spinner-small"></span>
      Submitting...
    `;
        try {
            const response = await fetch('/api/callback-request', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify(data),
            });
            const result = await response.json();
            if (response.ok && result.success) {
                this.renderSuccess();
            }
            else if (response.status === 409) {
                // Duplicate request
                this.showError('You already have a pending callback request. Please wait for our team to contact you.');
            }
            else {
                this.showError(result.message || 'Failed to submit request. Please try again.');
            }
        }
        catch (error) {
            console.error('Callback request error:', error);
            this.showError('Failed to submit request. Please try again.');
        }
        finally {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Request Callback';
        }
    }
    renderSuccess() {
        if (!this.modalContent)
            return;
        this.modalContent.innerHTML = `
      <div class="callback-modal-header">
        <h2>Request Submitted!</h2>
        <button type="button" class="callback-modal-close" aria-label="Close">&times;</button>
      </div>

      <div class="callback-modal-body">
        <div class="callback-success">
          <svg class="callback-success-icon" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          <h3>Thank you for your request!</h3>
          <p>Our team will contact you at your preferred time. You'll receive a confirmation email shortly.</p>
          <button type="button" class="callback-btn callback-btn-primary" id="callback-done-btn">
            Done
          </button>
        </div>
      </div>
    `;
        // Close button
        const closeBtn = this.modalContent?.querySelector('.callback-modal-close');
        closeBtn?.addEventListener('click', () => this.close());
        // Done button
        const doneBtn = document.getElementById('callback-done-btn');
        doneBtn?.addEventListener('click', () => this.close());
    }
    showError(message) {
        if (!this.modalContent)
            return;
        this.modalContent.innerHTML = `
      <div class="callback-modal-header">
        <h2>Error</h2>
        <button type="button" class="callback-modal-close" aria-label="Close">&times;</button>
      </div>

      <div class="callback-modal-body">
        <div class="callback-error">
          <svg class="callback-error-icon" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          <h3>Something went wrong</h3>
          <p>${message}</p>
          <div class="callback-error-actions">
            <button type="button" class="callback-btn callback-btn-secondary" id="callback-error-close-btn">
              Close
            </button>
            <button type="button" class="callback-btn callback-btn-primary" id="callback-error-retry-btn">
              Try Again
            </button>
          </div>
        </div>
      </div>
    `;
        // Close button
        const closeBtn = this.modalContent?.querySelector('.callback-modal-close');
        closeBtn?.addEventListener('click', () => this.close());
        // Error close button
        const errorCloseBtn = document.getElementById('callback-error-close-btn');
        errorCloseBtn?.addEventListener('click', () => this.close());
        // Retry button
        const retryBtn = document.getElementById('callback-error-retry-btn');
        retryBtn?.addEventListener('click', () => this.open());
    }
    getUserData() {
        // Try to get user data from session/localStorage
        const data = {};
        try {
            // Check if user is logged in (session data might be in a meta tag or window object)
            const userDataEl = document.querySelector('[data-user-info]');
            if (userDataEl) {
                const userInfo = JSON.parse(userDataEl.getAttribute('data-user-info') || '{}');
                // Combine first and last name for single name field
                const fullName = [userInfo.firstName, userInfo.lastName].filter(Boolean).join(' ');
                data.name = fullName;
                data.email = userInfo.email || '';
                data.phone = userInfo.phone || '';
                data.company = userInfo.company || '';
            }
        }
        catch (error) {
            // Silently fail - no pre-fill data available
        }
        return data;
    }
}
export default CallbackModal;
